import React, { useState, useEffect } from 'react';
import { Clock, FileText, ChevronDown, X } from 'lucide-react';
import { supabase } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';

interface OperationLog {
  id: string;
  adminId: string;
  adminName: string;
  adminRole: 'master' | 'sub';
  action: string;
  targetName: string;
  targetPhone: string;
  timestamp: string;
}

export default function OperationLogs() {
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [currentAdmin, setCurrentAdmin] = useState<any>(null);
  const [selectedAdmins, setSelectedAdmins] = useState<Set<string>>(new Set());
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: adminData } = await supabase.from('admin_users').select('*').eq('id', session.user.id).single();
        if (adminData) {
          setCurrentAdmin({ id: adminData.id, role: adminData.role });
        }
      }
      await loadLogs();
    };
    init();
  }, []);

  const loadLogs = async () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const { data } = await supabase
      .from('admin_operation_logs')
      .select('*')
      .gte('created_at', thirtyDaysAgo.toISOString())
      .order('created_at', { ascending: false });
    if (data) {
      const mappedLogs = data.map((log: any) => ({
        id: log.id,
        adminId: log.admin_id,
        adminName: log.admin_name,
        adminRole: log.admin_role === 'super' ? 'master' : 'sub',
        action: log.operation_type,
        targetName: log.target_user_name || '',
        targetPhone: '',
        timestamp: formatDateTime(log.created_at)
      }));
      setLogs(mappedLogs);
    }
  };

  const visibleLogs = logs.filter(log => {
    if (!currentAdmin) return false;
    if (currentAdmin.role === 'super') {
      if (log.adminRole === 'master') return log.adminId === currentAdmin.id;
      return true;
    }
    return log.adminId === currentAdmin.id;
  }).filter(log => {
    if (selectedAdmins.size === 0) return true;
    return selectedAdmins.has(log.adminName);
  });

  const uniqueAdmins = Array.from(new Set(logs.map(log => log.adminName)));

  const toggleAdmin = (name: string) => {
    setSelectedAdmins(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">操作日志</h1>
        <div className="relative">
          <button onClick={() => setShowFilter(!showFilter)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <FileText className="w-4 h-4" />
            筛选
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilter ? 'rotate-180' : ''}`} />
          </button>
          {showFilter && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-gray-700">按管理员筛选</span>
                <button onClick={() => setSelectedAdmins(new Set())} className="text-sm text-blue-600 hover:text-blue-700">清除</button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {uniqueAdmins.map(name => (
                  <button key={name} onClick={() => toggleAdmin(name)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm ${selectedAdmins.has(name) ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}>
                    {name}
                    {selectedAdmins.has(name) && <X className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">时间</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">管理员</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">角色</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">操作</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">目标用户</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visibleLogs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {log.timestamp}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{log.adminName}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${log.adminRole === 'master' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {log.adminRole === 'master' ? '主管理员' : '副管理员'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">{log.action}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{log.targetName}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibleLogs.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>暂无操作日志</p>
          </div>
        )}
      </div>
    </div>
  );
}
